import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { videoApiClient, JOB_STATES, STATE_DESCRIPTIONS } from '../services/videoApi';
import { directorApiClient } from '../services/directorApi';
import {
    getJobIdFromPath,
    navigationHelpers,
    historyUtils,
    debugUtils as urlDebugUtils,
    isValidJobId
} from '../utils/urlManager';
import {
    recoverJobState,
    continueJobPolling,
    RECOVERY_STATES,
    canRecoverJob,
    debugJobRecovery,
    isConfigurationPhase
} from '../utils/jobRecovery';
import { useOrganization } from '../hooks/useOrganization';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { DEFAULT_SCRIPT_MODE, normalizeScriptMode } from '@/lib/videoConfig';
import { isPaymentRequired } from '@/lib/featureFlags';
import { trackEvent, metaTrack, getMetaCookies } from '@/lib/analytics';

// Decomposed step components
import SearchStep from '../components/creatives/SearchStep';
import ConfigureStep from '../components/creatives/ConfigureStep';
import GenerateStep from '../components/creatives/GenerateStep';
import ReviewStep from '../components/creatives/ReviewStep';

// Mock data for demo mode
import {
    MOCK_BUSINESS,
    MOCK_PHOTOS,
    MOCK_REVIEWS,
    MOCK_CHARACTERS,
    MOCK_CAMPAIGN_OPTIONS,
    MOCK_CAMPAIGN_BRIEF,
    MOCK_VIDEO_CONFIG,
    MOCK_VIDEO_URL,
    MOCK_JOB_ID,
} from '../services/mockStudioData';

const CreativesStudio = () => {
    // React Router hooks
    const { jobId: urlJobId, orgSlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Organization context
    const { currentOrg } = useOrganization();

    // Existing state
    const [currentStep, setCurrentStep] = useState(1);
    const [searchMethod, setSearchMethod] = useState('search');
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [realBusinessData, setRealBusinessData] = useState(null);
    const [selectedPlaceId, setSelectedPlaceId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentStatus, setCurrentStatus] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState('Analyzing your business...');
    const [isComplete, setIsComplete] = useState(false);
    const [videoJobId, setVideoJobId] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [apiError, setApiError] = useState(null);
    const [generationError, setGenerationError] = useState(null);

    // Video configuration options
    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [duration, setDuration] = useState(15);
    const [scriptMode, setScriptMode] = useState(DEFAULT_SCRIPT_MODE);
    const [silentVideo, setSilentVideo] = useState(false);
    // Narration voice id (e.g. 'female-000'); null = automatic casting.
    const [voiceId, setVoiceId] = useState(null);

    // Photo management
    const [photos, setPhotos] = useState([]);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

    // Reviews selection
    const [reviews, setReviews] = useState([]);
    const [selectedReviews, setSelectedReviews] = useState([]);

    // Character references
    const [characters, setCharacters] = useState([]);

    // Scraping state
    const [isScraping, setIsScraping] = useState(false);
    const [scrapingJobId, setScrapingJobId] = useState(null);

    // Logo overlay configuration
    const [logoOverlayEnabled, setLogoOverlayEnabled] = useState(true);
    const [logoOverlayPosition, setLogoOverlayPosition] = useState('bottom-right');
    const [logoOverlayOpacity, setLogoOverlayOpacity] = useState(0.85);

    // Campaign brief state
    const [campaignBrief, setCampaignBrief] = useState({
        purpose: 'awareness',
        seasonal_theme: null,
        tone: 'auto',
        channel: 'auto',
        offers: [],
        notes: ''
    });

    // Campaign options from API
    const [campaignOptions, setCampaignOptions] = useState(null);
    const [campaignOptionsError, setCampaignOptionsError] = useState(null);

    // Advertiser selection
    const [selectedAdvertiserId, setSelectedAdvertiserId] = useState(null);

    // URL management state
    const [isRecovering, setIsRecovering] = useState(false);
    const [recoveryError, setRecoveryError] = useState(null);
    const [hasInitialized, setHasInitialized] = useState(false);

    const progressIntervalRef = useRef(null);

    // ── Helpers ──────────────────────────────────────────────────────────────

    const MOCK_GENERATION_ENABLED =
        import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true' ||
        window.location.search.includes('mockGeneration=true');

    const getOrgBasePath = () => {
        if (currentOrg) return `/org/${currentOrg.slug}`;
        if (orgSlug) return `/org/${orgSlug}`;
        return '';
    };

    const navigateToJob = (jobId) => {
        const basePath = getOrgBasePath();
        const jobPath = basePath ? `${basePath}/creatives/job/${jobId}` : `/creatives/job/${jobId}`;
        urlDebugUtils.logNavigation('Navigate to job', location.pathname, jobPath, jobId);
        navigate(jobPath);
    };

    const navigateToCreativesBase = (replace = true) => {
        const basePath = getOrgBasePath();
        const creativesPath = basePath ? `${basePath}/creatives` : '/creatives';
        urlDebugUtils.logNavigation('Navigate to base', location.pathname, creativesPath);
        navigate(creativesPath, { replace });
    };

    // ── Progress & Generation ────────────────────────────────────────────────

    // Shown to the user on any generation failure. We never surface raw
    // backend/network detail; the real error is sent to Sentry + the console.
    const GENERIC_GENERATION_ERROR =
        'Something went wrong on our end. We track these automatically — please try again, or contact us if the problem persists.';

    const updateProgressFromStatus = (status) => {
        console.debug('Video generation progress:', status);
        const progressPercent = status.progress?.percent || 0;
        setProgress(progressPercent);
        const stateDescription = STATE_DESCRIPTIONS[status.state] || status.state;
        setLoadingMessage(stateDescription);
        const statusMapping = {
            [JOB_STATES.ACCEPTED]: 0,
            [JOB_STATES.SCRAPING]: 0,
            [JOB_STATES.DATA_READY]: 1,
            [JOB_STATES.GENERATING]: 2,
            [JOB_STATES.RENDERING]: 3,
            [JOB_STATES.COMPLETED]: 4
        };
        const statusIndex = statusMapping[status.state] || 0;
        setCurrentStatus(statusIndex);
    };

    const finalizeSuccessfulGeneration = (result) => {
        const resolvedJobId = result.jobId || result.job_id || videoJobId;
        const videoUrlResult = result.assets?.video_url || result.video_url || (resolvedJobId ? videoApiClient.getAssetUrl(resolvedJobId) : null);
        setVideoUrl(videoUrlResult);
        setIsLoading(false);
        setIsComplete(true);
        setCurrentStep(4);
        setLoadingMessage('Video generation completed!');
        setProgress(100);
        setGenerationError(null);
        trackEvent('creative_generation_completed', { job_id: resolvedJobId });
    };

    const handleGenerationFailure = (error, jobId, friendlyMessage = null) => {
        // For real failures, capture the detail for us (Sentry + console) but
        // never show it to the user. `friendlyMessage` is for deliberate,
        // already-user-safe messages (e.g. a payment edge case).
        if (!friendlyMessage) {
            Sentry.captureException(error, { extra: { jobId } });
        }
        console.error('Video generation failed:', error);
        setGenerationError({
            title: "We couldn't finish your video",
            message: friendlyMessage || GENERIC_GENERATION_ERROR,
            jobId,
        });
        setIsLoading(false);
        setProgress(0);
        setCurrentStatus(0);
        setLoadingMessage('Video generation failed');
        setCurrentStep(3);
    };

    const pollExistingJob = async (jobId) => {
        return videoApiClient.pollJobStatus(jobId, { pollInterval: 2000, onProgress: updateProgressFromStatus });
    };

    // ── Business Data Loader ─────────────────────────────────────────────────

    const loadBusinessDataForJob = async (jobId) => {
        const businessResult = await directorApiClient.getBusinessData(jobId);
        const bData = businessResult.result
            ? (businessResult.result.business || businessResult.result.business_data || businessResult.result)
            : (businessResult.business || businessResult.business_data || businessResult);

        if (bData) {
            const loadedPhotos = (bData.photos || []).map(photo => ({
                ...photo,
                selected: photo.selected !== undefined ? photo.selected : true
            }));
            setPhotos(loadedPhotos);

            const loadedReviews = (bData.reviews || []).map((review, idx) => ({
                review_id: review.review_id || `review-${idx}`,
                text: typeof review === 'string' ? review : (review.text || review.content || ''),
                rating: typeof review === 'object' ? review.rating : null,
                author: typeof review === 'object' ? review.author : null,
                selected: review.selected !== undefined ? review.selected : true
            }));
            setReviews(loadedReviews);
            setSelectedReviews(loadedReviews.filter(r => r.selected).map(r => r.review_id));
            setRealBusinessData(prev => ({ ...prev, ...bData }));
        }

        try {
            const charResult = await directorApiClient.getCharacters(jobId);
            setCharacters(charResult.characters || []);
        } catch (charError) {
            console.debug('Could not load characters:', charError);
        }
    };

    // ── Google Place Selection ────────────────────────────────────────────────

    const handleGooglePlaceSelect = (businessInfo) => {
        setRealBusinessData(businessInfo);
        setSelectedBusiness(businessInfo);
        setSelectedPlaceId(businessInfo.placeId);
        console.debug('Selected place ID:', businessInfo.placeId);
    };

    // ── Start Scraping (Step 1 → Step 2) ─────────────────────────────────────

    const startScraping = async (source) => {
        if (!MOCK_GENERATION_ENABLED && !source?.place_id && !source?.website_url) {
            setApiError('Please select a business or enter a website URL');
            return;
        }

        setIsScraping(true);
        setApiError(null);

        // ── Mock mode: populate state from mock data, skip API ──────────
        if (MOCK_GENERATION_ENABLED) {
            await new Promise(r => setTimeout(r, 1200)); // simulate scraping delay
            setScrapingJobId(MOCK_JOB_ID);
            setRealBusinessData(MOCK_BUSINESS);
            setSelectedBusiness(MOCK_BUSINESS);
            setSelectedPlaceId(MOCK_BUSINESS.placeId);
            setPhotos(MOCK_PHOTOS.map(p => ({ ...p })));
            setReviews(MOCK_REVIEWS.map(r => ({ ...r })));
            setSelectedReviews(MOCK_REVIEWS.filter(r => r.selected).map(r => r.review_id));
            setCharacters(MOCK_CHARACTERS.map(c => ({ ...c })));
            setCampaignBrief({ ...MOCK_CAMPAIGN_BRIEF });
            setIsScraping(false);
            setCurrentStep(2);
            return;
        }

        try {
            const response = await directorApiClient.createProject(source, {
                advertiser_id: selectedAdvertiserId || undefined
            });
            const newJobId = response.job_id || response.jobId || (response.result && response.result.job_id);

            if (!newJobId) {
                setApiError('Could not create the project. Please try again.');
                return;
            }

            // Project created — move into the Configure step immediately. The
            // business data + brief load in the background so a slow or failing
            // load can't strand the user on the search step.
            setScrapingJobId(newJobId);
            navigateToJob(newJobId);
            setCurrentStep(2);
            setIsScraping(false);
            trackEvent('creative_business_analyzed', { job_id: newJobId });

            try {
                await loadBusinessDataForJob(newJobId);
                const briefResult = await directorApiClient.getCampaignBrief(newJobId);
                if (briefResult.brief) setCampaignBrief(briefResult.brief);
            } catch (loadError) {
                console.debug('Could not load business data/brief yet:', loadError);
            }
        } catch (error) {
            console.error('Failed to start scraping:', error);
            setApiError('Failed to analyze business. Please try again.');
        } finally {
            setIsScraping(false);
        }
    };

    // ── Photo Handlers ───────────────────────────────────────────────────────

    const handlePhotoToggle = (photoId, index) => {
        setPhotos(prev => prev.map((photo, i) => {
            if (photoId && photo.photo_id === photoId) return { ...photo, selected: !photo.selected };
            if (index !== undefined && i === index) return { ...photo, selected: !photo.selected };
            return photo;
        }));
    };

    const handlePhotoUpload = async (file) => {
        if (!scrapingJobId) {
            toast.error('No project available for photo upload');
            return;
        }
        setIsUploadingPhoto(true);
        try {
            const dataUrl = await directorApiClient.fileToDataUrl(file);

            if (MOCK_GENERATION_ENABLED) {
                const newPhoto = {
                    photo_id: `mock-photo-${Date.now()}`,
                    photo_name_id: file.name,
                    data_url: dataUrl,
                    caption: file.name,
                    selected: true,
                };
                setPhotos(prev => [...prev, newPhoto]);
                toast.success('Photo uploaded');
                setIsUploadingPhoto(false);
                return;
            }

            const response = await directorApiClient.uploadPhoto(scrapingJobId, dataUrl, file.name);
            const newPhoto = {
                photo_id: response.photo_id,
                photo_name_id: response.photo_name_id,
                data_url: dataUrl,
                caption: file.name,
                selected: true
            };
            setPhotos(prev => [...prev, newPhoto]);
            toast.success('Photo uploaded');
        } catch (error) {
            console.error('Failed to upload photo:', error);
            toast.error('Failed to upload photo');
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const handlePhotoDelete = async (photoId) => {
        if (!scrapingJobId) return;

        if (MOCK_GENERATION_ENABLED) {
            setPhotos(prev => prev.filter(photo => photo.photo_id !== photoId));
            toast.success('Photo deleted');
            return;
        }

        try {
            await directorApiClient.deletePhoto(scrapingJobId, photoId);
            setPhotos(prev => prev.filter(photo => photo.photo_id !== photoId));
            toast.success('Photo deleted');
        } catch (error) {
            console.error('Failed to delete photo:', error);
            toast.error('Failed to delete photo');
        }
    };

    // ── Review Handlers ──────────────────────────────────────────────────────

    const handleReviewToggle = (reviewId) => {
        setSelectedReviews(prev =>
            prev.includes(reviewId) ? prev.filter(id => id !== reviewId) : [...prev, reviewId]
        );
    };

    // ── Character Handlers ─────────────────────────────────────────────────

    const handleCharacterUpload = async (dataUrls, metadata = {}) => {
        if (!scrapingJobId) {
            toast.error('No project available for character upload');
            return;
        }
        if (characters.length >= 3) {
            toast.error('Maximum 3 characters allowed');
            return;
        }

        if (MOCK_GENERATION_ENABLED) {
            const mockChar = {
                character_id: `mock-char-${Date.now()}`,
                data_urls: dataUrls,
                image_url: dataUrls[0],
                name: metadata.name || '',
                role: metadata.role || '',
                description: metadata.description || '',
            };
            setCharacters(prev => [...prev, mockChar]);
            toast.success('Character added');
            return;
        }

        try {
            const response = await directorApiClient.uploadCharacter(scrapingJobId, {
                data_urls: dataUrls,
                ...metadata,
            });
            setCharacters(prev => [...prev, response]);
            toast.success('Character added');
        } catch (error) {
            console.error('Failed to upload character:', error);
            toast.error('Failed to upload character');
        }
    };

    const handleCharacterUpdate = async (characterId, updates) => {
        if (!scrapingJobId) return;

        if (MOCK_GENERATION_ENABLED) {
            setCharacters(prev => prev.map(c =>
                c.character_id === characterId ? { ...c, ...updates } : c
            ));
            toast.success('Character updated');
            return;
        }

        try {
            const response = await directorApiClient.updateCharacter(scrapingJobId, characterId, updates);
            setCharacters(prev => prev.map(c => c.character_id === characterId ? { ...c, ...response } : c));
            toast.success('Character updated');
        } catch (error) {
            console.error('Failed to update character:', error);
            toast.error('Failed to update character');
        }
    };

    const handleCharacterDelete = async (characterId) => {
        if (!scrapingJobId) return;

        if (MOCK_GENERATION_ENABLED) {
            setCharacters(prev => prev.filter(c => c.character_id !== characterId));
            toast.success('Character removed');
            return;
        }

        try {
            await directorApiClient.deleteCharacter(scrapingJobId, characterId);
            setCharacters(prev => prev.filter(c => c.character_id !== characterId));
            toast.success('Character removed');
        } catch (error) {
            console.error('Failed to delete character:', error);
            toast.error('Failed to delete character');
        }
    };

    // ── Logo Handlers ────────────────────────────────────────────────────────

    const handleLogoUpload = async (file) => {
        if (!scrapingJobId) {
            toast.error('No project available for logo upload');
            return;
        }
        try {
            const dataUrl = await directorApiClient.fileToDataUrl(file);

            if (MOCK_GENERATION_ENABLED) {
                setRealBusinessData(prev => ({ ...prev, logo_data_url: dataUrl }));
                toast.success('Logo uploaded');
                return;
            }

            const response = await directorApiClient.uploadLogo(scrapingJobId, dataUrl);
            setRealBusinessData(prev => ({ ...prev, logo_data_url: response.logo_data_url }));
            toast.success('Logo uploaded');
        } catch (error) {
            console.error('Failed to upload logo:', error);
            toast.error('Failed to upload logo');
        }
    };

    const handleLogoDelete = async () => {
        if (!scrapingJobId) return;

        if (MOCK_GENERATION_ENABLED) {
            setRealBusinessData(prev => ({ ...prev, logo_data_url: null }));
            toast.success('Logo removed');
            return;
        }

        try {
            await directorApiClient.deleteLogo(scrapingJobId);
            setRealBusinessData(prev => ({ ...prev, logo_data_url: null }));
            toast.success('Logo removed');
        } catch (error) {
            console.error('Failed to delete logo:', error);
            toast.error('Failed to delete logo');
        }
    };

    // ── Save Configuration ───────────────────────────────────────────────────

    const saveConfiguration = async () => {
        if (MOCK_GENERATION_ENABLED) {
            console.debug('[Mock] Configuration saved locally');
            return true;
        }
        if (!scrapingJobId) {
            console.warn('No scrapingJobId available, skipping configuration save');
            return true;
        }
        try {
            const selectedPhotoIds = photos.filter(p => p.selected).map(p => p.photo_id).filter(Boolean);
            const selectedReviewIds = reviews.filter(r => selectedReviews.includes(r.review_id)).map(r => r.review_id).filter(Boolean);

            await directorApiClient.updateBusinessData(scrapingJobId, {
                selected_photo_ids: selectedPhotoIds,
                selected_review_ids: selectedReviewIds
            });
            await directorApiClient.updateVideoConfig(scrapingJobId, {
                aspect_ratio: aspectRatio,
                length: duration,
                script_mode: scriptMode,
                voice_id: voiceId,
                logo_overlay_enabled: logoOverlayEnabled,
                logo_overlay_position: logoOverlayPosition,
                logo_overlay_opacity: logoOverlayOpacity
            });
            await directorApiClient.updateCampaignBrief(scrapingJobId, campaignBrief);
            return true;
        } catch (error) {
            console.error('Failed to save configuration:', error);
            setApiError('Failed to save configuration. Please try again.');
            return false;
        }
    };

    // ── Paid Checkout (Step 2 → Stripe → Step 3) ─────────────────────────────

    // Saves the creative config, then redirects to Stripe Checkout. Generation
    // does NOT start here — the back-end starts it only after it confirms the
    // payment (via webhook or the secure confirmPayment check on return).
    const startPaidCheckout = async () => {
        if (!scrapingJobId) {
            setApiError('Please complete the “Analyze Business” step before paying.');
            return;
        }
        setApiError(null);
        setIsLoading(true);
        setLoadingMessage('Saving your creative…');
        try {
            const saved = await saveConfiguration();
            if (!saved) {
                setIsLoading(false);
                setApiError('Could not save your configuration. Please try again.');
                return;
            }
            setLoadingMessage('Redirecting to secure checkout…');
            const basePath = getOrgBasePath();
            const jobPath = basePath
                ? `${basePath}/creatives/job/${scrapingJobId}`
                : `/creatives/job/${scrapingJobId}`;
            const origin = window.location.origin;
            const successUrl = `${origin}${jobPath}?payment=success&session_id={CHECKOUT_SESSION_ID}`;
            const cancelUrl = `${origin}${jobPath}?payment=cancelled`;
            const { fbp, fbc } = getMetaCookies();
            const { checkoutUrl } = await videoApiClient.createCheckoutSession(scrapingJobId, {
                successUrl,
                cancelUrl,
                fbp,
                fbc,
            });
            if (!checkoutUrl) {
                throw new Error('No checkout URL was returned. Please try again.');
            }
            trackEvent('creative_checkout_started');
            metaTrack('InitiateCheckout', {}, { eventID: `initiatecheckout_${scrapingJobId}` });
            window.location.href = checkoutUrl; // leaves the app for Stripe
        } catch (error) {
            Sentry.captureException(error);
            console.error('Checkout failed:', error);
            setIsLoading(false);
            setApiError("We couldn't start checkout. Please try again.");
        }
    };

    // ── Start Generation (Step 2 → Step 3) ───────────────────────────────────

    const startGeneration = async () => {
        if (!scrapingJobId && !selectedPlaceId) {
            setApiError('Please select a business first');
            return;
        }
        // Grounded mode anchors every scene to a real photo, so it needs enough
        // of them (0 = all-AI, otherwise at least 3). Creative mode composes
        // freely, so any photo count is fine.
        const selectedPhotosCount = photos.filter(p => p.selected).length;
        if (scriptMode === 'grounded' && selectedPhotosCount > 0 && selectedPhotosCount < 3) {
            setApiError('Select at least 3 photos, or deselect all for AI-generated visuals');
            return;
        }

        trackEvent('creative_generate_clicked', {
            paid: isPaymentRequired(),
            aspect_ratio: aspectRatio,
            duration,
        });

        // Paid MVP: gate generation behind Stripe Checkout. Mock mode bypasses.
        if (isPaymentRequired() && !MOCK_GENERATION_ENABLED) {
            await startPaidCheckout();
            return;
        }

        setCurrentStep(3);
        setIsLoading(true);
        setProgress(0);
        setCurrentStatus(0);
        setApiError(null);
        setLoadingMessage('Starting video generation...');
        setGenerationError(null);

        let pendingJobId = null;

        try {
            const saved = await saveConfiguration();
            if (!saved) {
                setCurrentStep(2);
                setIsLoading(false);
                return;
            }

            if (MOCK_GENERATION_ENABLED) {
                setVideoJobId(MOCK_JOB_ID);
                setTimeout(() => updateProgressFromStatus({ state: JOB_STATES.SCRAPING, progress: { percent: 15 } }), 800);
                setTimeout(() => updateProgressFromStatus({ state: JOB_STATES.GENERATING, progress: { percent: 55 } }), 2200);
                setTimeout(() => updateProgressFromStatus({ state: JOB_STATES.RENDERING, progress: { percent: 85 } }), 4000);
                setTimeout(() => finalizeSuccessfulGeneration({
                    video_url: MOCK_VIDEO_URL,
                    assets: { video_url: MOCK_VIDEO_URL }
                }), 5000);
                return;
            }

            const videoConfigOverrides = {
                aspect_ratio: aspectRatio,
                length: duration,
                script_mode: scriptMode,
                voice_id: voiceId,
                logo_overlay_enabled: logoOverlayEnabled,
                logo_overlay_position: logoOverlayPosition,
                logo_overlay_opacity: logoOverlayOpacity
            };

            if (scrapingJobId) {
                pendingJobId = scrapingJobId;
                setVideoJobId(scrapingJobId);
                await videoApiClient.restartVideoJob(scrapingJobId);
                const result = await pollExistingJob(scrapingJobId);
                finalizeSuccessfulGeneration(result);
            } else {
                const job = await videoApiClient.createVideoJob({
                    source: { place_id: selectedPlaceId },
                    campaign: { objective: 'brand_awareness', distribution_channel: aspectRatio === '9:16' ? 'social' : 'CTV' },
                    metadata: { requested_by: 'cornercast-frontend', locale: 'en-US' },
                    video_config_overrides: videoConfigOverrides
                });
                pendingJobId = job.jobId;
                setVideoJobId(job.jobId);
                console.debug('Video job created:', job);
                navigateToJob(job.jobId);
                const result = await pollExistingJob(job.jobId);
                finalizeSuccessfulGeneration(result);
            }
        } catch (error) {
            handleGenerationFailure(error, pendingJobId || videoJobId);
        }
    };

    // ── Retry Generation ─────────────────────────────────────────────────────

    const handleRetryGeneration = async () => {
        if (!videoJobId) { startGeneration(); return; }

        setCurrentStep(3);
        setIsLoading(true);
        setProgress(0);
        setCurrentStatus(0);
        setLoadingMessage('Retrying video generation...');
        setGenerationError(null);

        try {
            await videoApiClient.retryVideoJob(videoJobId);
            const result = await pollExistingJob(videoJobId);
            finalizeSuccessfulGeneration(result);
        } catch (error) {
            if (error.statusCode === 400 && error.details && typeof error.details.detail === 'string' && error.details.detail.includes('Job is already in progress and cannot be retried')) {
                console.debug('Job already in progress, continuing with polling...');
                try {
                    const result = await pollExistingJob(videoJobId);
                    finalizeSuccessfulGeneration(result);
                } catch (pollError) {
                    handleGenerationFailure(pollError, videoJobId);
                }
            } else {
                handleGenerationFailure(error, videoJobId);
            }
        }
    };

    // ── Reset Workflow ───────────────────────────────────────────────────────

    const resetWorkflow = () => {
        setCurrentStep(1);
        setIsLoading(false);
        setIsComplete(false);
        setProgress(0);
        setCurrentStatus(0);
        setSelectedBusiness(null);
        setRealBusinessData(null);
        setSelectedPlaceId(null);
        setVideoJobId(null);
        setVideoUrl(null);
        setApiError(null);
        setGenerationError(null);
        setIsRecovering(false);
        setRecoveryError(null);
        setAspectRatio('16:9');
        setDuration(15);
        setScriptMode(DEFAULT_SCRIPT_MODE);
        setSilentVideo(false);
        setPhotos([]);
        setReviews([]);
        setSelectedReviews([]);
        setCharacters([]);
        setScrapingJobId(null);
        setLogoOverlayEnabled(true);
        setLogoOverlayPosition('bottom-right');
        setLogoOverlayOpacity(0.85);
        setCampaignBrief({ purpose: 'awareness', seasonal_theme: null, tone: 'auto', channel: 'auto', offers: [], notes: '' });
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        navigateToCreativesBase(true);
    };

    // ── Job Recovery ─────────────────────────────────────────────────────────

    const handleJobRecovery = async (jobId) => {
        if (!canRecoverJob(jobId)) {
            setRecoveryError('Invalid job ID format');
            setIsRecovering(false);
            return false;
        }

        setIsRecovering(true);
        setRecoveryError(null);

        try {
            urlDebugUtils.logNavigation('Job recovery started', location.pathname, location.pathname, jobId);
            const recovery = await recoverJobState(jobId);
            debugJobRecovery.logRecoveryAttempt(jobId, recovery);

            if (recovery.state === RECOVERY_STATES.SUCCESS) {
                setVideoJobId(jobId);
                setCurrentStep(recovery.uiState.currentStep);
                setIsLoading(recovery.uiState.isLoading);
                setIsComplete(recovery.uiState.isComplete);
                setProgress(recovery.uiState.progress);
                setCurrentStatus(recovery.uiState.currentStatus);
                setLoadingMessage(recovery.uiState.loadingMessage);
                setGenerationError(recovery.uiState.generationError);
                setVideoUrl(recovery.uiState.videoUrl);

                if (recovery.businessInfo) {
                    setRealBusinessData(recovery.businessInfo);
                    setSelectedBusiness(recovery.businessInfo);
                    setSelectedPlaceId(recovery.businessInfo.placeId);
                }

                if (isConfigurationPhase(recovery.jobStatus?.state)) {
                    setScrapingJobId(jobId);
                    try {
                        await loadBusinessDataForJob(jobId);
                        setScriptMode(DEFAULT_SCRIPT_MODE);
                        try {
                            const videoConfigResult = await directorApiClient.getVideoConfig(jobId);
                            const videoConfig = videoConfigResult?.result || videoConfigResult;
                            if (videoConfig) {
                                if (videoConfig.aspect_ratio) setAspectRatio(videoConfig.aspect_ratio);
                                if (videoConfig.length) setDuration(videoConfig.length);
                                setScriptMode(normalizeScriptMode(videoConfig.script_mode));
                                setVoiceId(videoConfig.voice_id ?? null);
                                if (videoConfig.logo_overlay_enabled !== undefined) setLogoOverlayEnabled(videoConfig.logo_overlay_enabled);
                                if (videoConfig.logo_overlay_position) setLogoOverlayPosition(videoConfig.logo_overlay_position);
                                if (videoConfig.logo_overlay_opacity !== undefined) setLogoOverlayOpacity(videoConfig.logo_overlay_opacity);
                            }
                        } catch (configError) {
                            console.debug('Could not load video config:', configError);
                        }
                        try {
                            const briefResult = await directorApiClient.getCampaignBrief(jobId);
                            if (briefResult.brief) setCampaignBrief(briefResult.brief);
                        } catch (briefError) {
                            console.debug('Could not load campaign brief:', briefError);
                        }
                    } catch (businessError) {
                        console.error('Failed to load business data for configuration:', businessError);
                    }
                }

                if (recovery.uiState.isLoading) {
                    setIsRecovering(false);
                    try {
                        const result = await continueJobPolling(jobId, updateProgressFromStatus);
                        finalizeSuccessfulGeneration(result);
                    } catch (pollingError) {
                        handleGenerationFailure(pollingError, jobId);
                    }
                    return true;
                }

                setIsRecovering(false);
                return true;
            } else {
                setRecoveryError(recovery.error || 'Unable to recover job');
                setIsRecovering(false);
                return false;
            }
        } catch (error) {
            debugJobRecovery.logRecoveryError(jobId, error);
            setRecoveryError('Failed to recover job: ' + error.message);
            setIsRecovering(false);
            return false;
        }
    };

    // ── Payment Return (Stripe success / cancel) ─────────────────────────────

    // Handles the redirect back from Stripe Checkout. On cancel, returns the
    // user to the configure step. On success, securely confirms the payment
    // server-side (never trusting the URL alone) and then tracks generation.
    const handlePaymentReturn = async (jobId, params) => {
        const paymentParam = params.get('payment');
        const basePath = getOrgBasePath();
        const jobPath = basePath
            ? `${basePath}/creatives/job/${jobId}`
            : `/creatives/job/${jobId}`;

        // Strip the payment query params so a refresh doesn't reprocess them.
        navigate(jobPath, { replace: true });

        if (paymentParam === 'cancelled') {
            toast.message('Payment cancelled — your creative was not generated.');
            await handleJobRecovery(jobId); // rehydrate config (returns to step 2)
            return;
        }

        // paymentParam === 'success'
        const sessionId = params.get('session_id');
        setVideoJobId(jobId);
        setScrapingJobId(jobId);
        setCurrentStep(3);
        setIsLoading(true);
        setProgress(0);
        setCurrentStatus(0);
        setLoadingMessage('Confirming your payment…');
        setGenerationError(null);

        try {
            let progressing = true;
            if (sessionId) {
                const confirmation = await videoApiClient.confirmPayment(jobId, sessionId);
                if (!confirmation.paid) {
                    toast.error("We couldn't verify your payment. If you were charged, please contact support.");
                    await handleJobRecovery(jobId);
                    return;
                }
                trackEvent('creative_payment_confirmed', { job_id: jobId });
                // Meta Purchase — same event_id as the server-side CAPI event so Meta de-dupes them.
                metaTrack('Purchase', { currency: 'USD' }, { eventID: `purchase_${jobId}` });
                // Use the server's signals to avoid polling a job that never
                // started generating (e.g. a terminal dispatch rejection):
                // generation_started, or a job state that is already progressing.
                const progressingStates = [JOB_STATES.GENERATING, JOB_STATES.RENDERING, JOB_STATES.COMPLETED];
                progressing = confirmation.generationStarted || progressingStates.includes(confirmation.jobState);
            }
            if (!progressing) {
                handleGenerationFailure(
                    null,
                    jobId,
                    "Payment received, but generation didn't start. You can retry below — you won't be charged again.",
                );
                return;
            }
            // Generation is running server-side (confirm or webhook); track it.
            const result = await pollExistingJob(jobId);
            finalizeSuccessfulGeneration(result);
        } catch (error) {
            handleGenerationFailure(error, jobId);
        }
    };

    // ── Effects ──────────────────────────────────────────────────────────────

    useEffect(() => {
        const initializeComponent = async () => {
            if (hasInitialized) return;
            if (urlJobId && isValidJobId(urlJobId)) {
                const params = new URLSearchParams(window.location.search);
                if (params.get('payment')) {
                    await handlePaymentReturn(urlJobId, params);
                    setHasInitialized(true);
                    return;
                }
                const recovered = await handleJobRecovery(urlJobId);
                if (!recovered) {
                    // Recovery fails when the job belongs to a different org
                    // (backend 404s the whole job-scoped surface) or is gone.
                    // The silent redirect used to leave users wondering why
                    // they bounced out — surface a single toast so the cause
                    // is visible.
                    toast.error("We couldn't open that creative — it may belong to a different organization or no longer exist.");
                    navigateToCreativesBase(true);
                }
            } else if (urlJobId && !isValidJobId(urlJobId)) {
                navigateToCreativesBase(true);
            }
            setHasInitialized(true);
        };
        initializeComponent();
    }, [urlJobId, hasInitialized]);

    useEffect(() => {
        const fetchCampaignOptions = async () => {
            if (MOCK_GENERATION_ENABLED) {
                setCampaignOptions(MOCK_CAMPAIGN_OPTIONS);
                setCampaignOptionsError(null);
                return;
            }
            try {
                const options = await directorApiClient.getCampaignOptions();
                setCampaignOptions(options);
                setCampaignOptionsError(null);
            } catch (error) {
                console.error('Failed to load campaign options:', error);
                setCampaignOptionsError('Failed to load campaign configuration. Please refresh the page.');
            }
        };
        fetchCampaignOptions();
    }, []);

    useEffect(() => {
        const handleBrowserNavigation = async (pathname) => {
            const jobId = getJobIdFromPath(pathname);
            if (jobId && isValidJobId(jobId) && jobId !== videoJobId) {
                await handleJobRecovery(jobId);
            } else if (!jobId && videoJobId) {
                setCurrentStep(1);
                setIsLoading(false);
                setIsComplete(false);
                setVideoJobId(null);
                setVideoUrl(null);
                setGenerationError(null);
            }
        };

        let cleanup = null;
        if (historyUtils.isSupported()) {
            cleanup = historyUtils.addNavigationListener(handleBrowserNavigation);
        }
        return () => {
            if (cleanup) cleanup();
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
    }, [videoJobId]);

    // ── Recovery States ──────────────────────────────────────────────────────

    if (isRecovering) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                    <Spinner size="lg" className="mx-auto mb-4" />
                    <p className="text-lg font-medium text-foreground">Loading your project...</p>
                    <p className="text-sm text-muted-foreground mt-1">Please wait</p>
                </div>
            </div>
        );
    }

    if (recoveryError && hasInitialized) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center max-w-md">
                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">Unable to Resume Job</h3>
                    <p className="text-muted-foreground mb-6">{recoveryError}</p>
                    <Button onClick={() => navigateToCreativesBase(true)}>
                        Return to Search
                    </Button>
                </div>
            </div>
        );
    }

    // ── Main Render ──────────────────────────────────────────────────────────

    // Progress bar value per step (Step 3 uses actual generation progress)
    const globalProgress = currentStep === 1 ? 5
        : currentStep === 2 ? 35
        : currentStep === 3 ? Math.max(40, 40 + (progress * 0.55))
        : 100;

    return (
        <div className="min-h-screen bg-background">
            {/* Global Progress Bar */}
            <div className="fixed top-16 left-0 right-0 z-40 h-1 bg-muted">
                <div
                    className="h-full bg-gradient-to-r from-coral to-teal transition-all duration-500 ease-out"
                    style={{ width: `${globalProgress}%` }}
                />
            </div>

            {currentStep === 1 && (
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                    <SearchStep
                        searchMethod={searchMethod}
                        setSearchMethod={setSearchMethod}
                        selectedPlaceId={selectedPlaceId}
                        realBusinessData={realBusinessData}
                        isScraping={isScraping}
                        apiError={apiError}
                        currentOrg={currentOrg}
                        selectedAdvertiserId={selectedAdvertiserId}
                        setSelectedAdvertiserId={setSelectedAdvertiserId}
                        onPlaceSelect={handleGooglePlaceSelect}
                        onStartScraping={startScraping}
                        mockMode={MOCK_GENERATION_ENABLED}
                    />
                </div>
            )}

            {currentStep === 2 && (
                <ConfigureStep
                    realBusinessData={realBusinessData}
                    currentOrg={currentOrg}
                    aspectRatio={aspectRatio}
                    setAspectRatio={setAspectRatio}
                    duration={duration}
                    setDuration={setDuration}
                    scriptMode={scriptMode}
                    setScriptMode={setScriptMode}
                    voiceId={voiceId}
                    setVoiceId={setVoiceId}
                    silentVideo={silentVideo}
                    setSilentVideo={setSilentVideo}
                    campaignBrief={campaignBrief}
                    setCampaignBrief={setCampaignBrief}
                    campaignOptions={campaignOptions}
                    campaignOptionsError={campaignOptionsError}
                    photos={photos}
                    isUploadingPhoto={isUploadingPhoto}
                    onPhotoToggle={handlePhotoToggle}
                    onPhotoUpload={handlePhotoUpload}
                    onPhotoDelete={handlePhotoDelete}
                    reviews={reviews}
                    selectedReviews={selectedReviews}
                    onReviewToggle={handleReviewToggle}
                    characters={characters}
                    onCharacterUpload={handleCharacterUpload}
                    onCharacterUpdate={handleCharacterUpdate}
                    onCharacterDelete={handleCharacterDelete}
                    logoDataUrl={realBusinessData?.logo_data_url}
                    onLogoUpload={handleLogoUpload}
                    onLogoDelete={handleLogoDelete}
                    logoOverlayEnabled={logoOverlayEnabled}
                    setLogoOverlayEnabled={setLogoOverlayEnabled}
                    logoOverlayPosition={logoOverlayPosition}
                    setLogoOverlayPosition={setLogoOverlayPosition}
                    logoOverlayOpacity={logoOverlayOpacity}
                    setLogoOverlayOpacity={setLogoOverlayOpacity}
                    apiError={apiError}
                    onBack={() => setCurrentStep(1)}
                    onGenerate={startGeneration}
                    isGenerating={isLoading}
                    generateButtonLabel={isPaymentRequired() ? 'Pay & Generate Video' : undefined}
                    generateButtonLoadingLabel={isPaymentRequired() ? 'Starting secure checkout…' : undefined}
                />
            )}

            {currentStep === 3 && (
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                    <GenerateStep
                        isLoading={isLoading}
                        progress={progress}
                        currentStatus={currentStatus}
                        generationError={generationError}
                        onRetry={handleRetryGeneration}
                        onReset={resetWorkflow}
                    />
                </div>
            )}

            {currentStep === 4 && isComplete && (
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                    <ReviewStep
                        videoUrl={videoUrl}
                        videoJobId={videoJobId}
                        realBusinessData={realBusinessData}
                        selectedBusiness={selectedBusiness}
                        aspectRatio={aspectRatio}
                        duration={duration}
                        onReset={resetWorkflow}
                        mockMode={MOCK_GENERATION_ENABLED}
                    />
                </div>
            )}
        </div>
    );
};

export default CreativesStudio;
